import type { ChildGender } from '../types/profile'

/**
 * IMPORTANTE: las opciones se identifican por una CLAVE estable e independiente
 * del idioma. Es la clave lo que se guarda en el perfil y se envía a Google
 * Sheets, para que una respuesta en español y la misma en inglés lleguen como
 * el mismo dato. El texto visible vive en data/copy.
 */

export type InterestKey =
  | 'dinosaurs'
  | 'robots'
  | 'space'
  | 'vehicles'
  | 'animals'
  | 'drawing'
  | 'sports'
  | 'nature'
  | 'puzzles'
  | 'stories'
  | 'fantasy'
  | 'music'
  | 'princesses'
  | 'fairies'
  | 'other'

export type PersonalityKey =
  | 'curious'
  | 'imaginative'
  | 'sensitive'
  | 'energetic'
  | 'calm'
  | 'social'
  | 'brave'
  | 'shy'
  | 'determined'
  | 'thoughtful'
  | 'independent'
  | 'affectionate'

export type PriorityKey = 'confidence' | 'calm' | 'perseverance' | 'focus' | 'creativity'

export type SituationKey =
  | 'not_working'
  | 'frustrated'
  | 'afraid_to_try'
  | 'learning_new'
  | 'loses_focus'
  | 'gives_up'
  | 'self_doubt'
  | 'none'

export interface OptionItem<TKey extends string> {
  key: TKey
  emoji: string
}

export const OTHER_INTEREST: InterestKey = 'other'
export const NONE_SITUATION: SituationKey = 'none'

const BOY_FIRST: InterestKey[] = ['dinosaurs', 'robots', 'space', 'vehicles']
const GIRL_FIRST: InterestKey[] = ['princesses', 'fairies', 'animals', 'drawing']

const ALL_INTERESTS: OptionItem<InterestKey>[] = [
  { key: 'dinosaurs', emoji: '🦖' },
  { key: 'robots', emoji: '🤖' },
  { key: 'space', emoji: '🚀' },
  { key: 'vehicles', emoji: '🚗' },
  { key: 'animals', emoji: '🐶' },
  { key: 'drawing', emoji: '🎨' },
  { key: 'sports', emoji: '⚽' },
  { key: 'nature', emoji: '🌳' },
  { key: 'puzzles', emoji: '🧩' },
  { key: 'stories', emoji: '📚' },
  { key: 'fantasy', emoji: '🧙' },
  { key: 'music', emoji: '🎵' },
  { key: 'princesses', emoji: '👑' },
  { key: 'fairies', emoji: '🧚' },
]

/** Todas las opciones siguen disponibles: solo cambia el orden de prioridad. */
export function getInterestOptions(gender: ChildGender): OptionItem<InterestKey>[] {
  const priorityKeys = gender === 'daughter' ? GIRL_FIRST : BOY_FIRST
  const priority = priorityKeys
    .map((key) => ALL_INTERESTS.find((option) => option.key === key))
    .filter((option): option is OptionItem<InterestKey> => Boolean(option))
  const rest = ALL_INTERESTS.filter((option) => !priorityKeys.includes(option.key))
  return [...priority, ...rest, { key: OTHER_INTEREST, emoji: '✨' }]
}

export const AGE_OPTIONS = ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13+']

export const PERSONALITY_OPTIONS: OptionItem<PersonalityKey>[] = [
  { key: 'curious', emoji: '💡' },
  { key: 'imaginative', emoji: '🌈' },
  { key: 'sensitive', emoji: '💜' },
  { key: 'energetic', emoji: '⚡' },
  { key: 'calm', emoji: '🌱' },
  { key: 'social', emoji: '😊' },
  { key: 'brave', emoji: '🦁' },
  { key: 'shy', emoji: '🙈' },
  { key: 'determined', emoji: '🎯' },
  { key: 'thoughtful', emoji: '🤔' },
  { key: 'independent', emoji: '✨' },
  { key: 'affectionate', emoji: '🤗' },
]

export const PRIORITY_OPTIONS: OptionItem<PriorityKey>[] = [
  { key: 'confidence', emoji: '⭐' },
  { key: 'calm', emoji: '☁️' },
  { key: 'perseverance', emoji: '⚡' },
  { key: 'focus', emoji: '🎯' },
  { key: 'creativity', emoji: '💡' },
]

export const SITUATION_OPTIONS: SituationKey[] = [
  'not_working',
  'frustrated',
  'afraid_to_try',
  'learning_new',
  'loses_focus',
  'gives_up',
  'self_doubt',
  'none',
]

export const PERSONALITY_MAX = 4
export const PRIORITIES_MAX = 2
